/**
 * Форма тех. операции — создание и правка (AK-525).
 *
 * Поля: тип операции (локальный справочник компании) · название · плановые
 * даты и площадь · фактические даты и площадь · комментарий.
 *
 * Факт-поля показываем только когда операция уже не «запланирована» (или при
 * создании задним числом) — иначе форма создания заваливает пользователя
 * шестью пустыми полями, из которых нужны два.
 *
 * Статус здесь НЕ редактируется: он меняется кнопками-действиями
 * (`OperationStatusActions`), которые ходят по графу переходов с бэка.
 * Select со всеми четырьмя значениями позволил бы кликнуть нелегальный переход
 * и получить 422 — ровно то, что мы чинили в AK-524.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useFieldOptions,
  type CompanyOpType,
  type Operation,
  type OperationPatch,
} from './operationsApi';

interface Props {
  open: boolean;
  /** Редактируемая операция; `null` — создание. */
  operation: Operation | null;
  opTypes: CompanyOpType[];
  /**
   * Поле известно из контекста (создание из карточки поля) — тогда выбора поля
   * в форме нет. `undefined` при создании = показываем поиск по полям
   * (вход из общего списка операций, где контекста нет).
   */
  fixedFieldId?: string | null;
  saving?: boolean;
  error?: string | null;
  onSubmit: (
    body: OperationPatch & {
      company_op_type_id: string;
      name: string;
      field_id?: string;
    },
  ) => void;
  onClose: () => void;
}

function numOrNull(v: string): number | null {
  const t = v.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function OperationFormDialog({
  open,
  operation,
  opTypes,
  fixedFieldId,
  saving,
  error,
  onSubmit,
  onClose,
}: Props) {
  const [fieldId, setFieldId] = useState('');
  const [fieldSearch, setFieldSearch] = useState('');
  const [debouncedFieldSearch, setDebouncedFieldSearch] = useState('');
  const [cotId, setCotId] = useState('');
  const [name, setName] = useState('');
  const [planStart, setPlanStart] = useState('');
  const [planEnd, setPlanEnd] = useState('');
  const [factStart, setFactStart] = useState('');
  const [factEnd, setFactEnd] = useState('');
  const [planArea, setPlanArea] = useState('');
  const [factArea, setFactArea] = useState('');
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const [showFact, setShowFact] = useState(false);

  // Поле выбирается только при создании «из списка операций».
  const needsFieldPick = !operation && !fixedFieldId;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFieldSearch(fieldSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [fieldSearch]);

  const { data: fieldOptions } = useFieldOptions(
    debouncedFieldSearch,
    open && needsFieldPick,
  );

  useEffect(() => {
    if (!open) return;
    setFieldId('');
    setFieldSearch('');
    setCotId(operation?.company_op_type_id ?? '');
    setName(operation?.name ?? '');
    setPlanStart(operation?.planned_start_date ?? '');
    setPlanEnd(operation?.planned_end_date ?? '');
    setFactStart(operation?.actual_start_date ?? '');
    setFactEnd(operation?.actual_end_date ?? '');
    setPlanArea(operation?.planned_area_ha != null ? String(operation.planned_area_ha) : '');
    setFactArea(operation?.actual_area_ha != null ? String(operation.actual_area_ha) : '');
    setComment(operation?.comment ?? '');
    setTouched(false);
    setShowFact(
      !!operation && operation.lifecycle_state !== 'pending',
    );
  }, [open, operation]);

  const selectedType = useMemo(
    () => opTypes.find((t) => t.id === cotId) ?? null,
    [opTypes, cotId],
  );

  // Название по умолчанию = название типа операции: агроному не нужно
  // придумывать текст для «Вспашка», но переименовать он может.
  const effectiveName = name.trim() || selectedType?.local_name || '';
  const valid = !!cotId && effectiveName !== '' && (!needsFieldPick || !!fieldId);

  const submit = () => {
    setTouched(true);
    if (!valid) return;
    onSubmit({
      ...(needsFieldPick ? { field_id: fieldId } : {}),
      company_op_type_id: cotId,
      name: effectiveName,
      planned_start_date: planStart || null,
      planned_end_date: planEnd || null,
      planned_area_ha: numOrNull(planArea),
      ...(showFact
        ? {
            actual_start_date: factStart || null,
            actual_end_date: factEnd || null,
            actual_area_ha: numOrNull(factArea),
          }
        : {}),
      comment: comment.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {operation ? 'Правка операции' : 'Новая тех. операция'}
          </DialogTitle>
          <DialogDescription>
            Что делаем на поле, когда планируем и на какой площади.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {needsFieldPick && (
            <div className="grid gap-2">
              <Label htmlFor="op-field">Поле</Label>
              <Input
                id="op-field-search"
                placeholder="Поиск поля по названию"
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
              />
              <Select value={fieldId} onValueChange={setFieldId}>
                <SelectTrigger id="op-field">
                  <SelectValue placeholder="Выберите поле" />
                </SelectTrigger>
                <SelectContent>
                  {(fieldOptions ?? []).map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                      {f.farm_name ? ` · ${f.farm_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {touched && !fieldId && (
                <p className="text-xs text-destructive">Выберите поле.</p>
              )}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="op-type">Тип операции</Label>
            <Select value={cotId} onValueChange={setCotId}>
              <SelectTrigger id="op-type">
                <SelectValue placeholder="Выберите тип" />
              </SelectTrigger>
              <SelectContent>
                {opTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.local_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {touched && !cotId && (
              <p className="text-xs text-destructive">Выберите тип операции.</p>
            )}
            {opTypes.length === 0 && (
              <p className="text-xs text-muted-foreground">
                В справочнике компании ещё нет типов операций — заведите их в
                настройках компании.
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="op-name">Название</Label>
            <Input
              id="op-name"
              placeholder={selectedType?.local_name ?? 'Например, Вспашка зяби'}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="op-plan-start">План: начало</Label>
              <Input
                id="op-plan-start"
                type="date"
                value={planStart}
                onChange={(e) => setPlanStart(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="op-plan-end">План: окончание</Label>
              <Input
                id="op-plan-end"
                type="date"
                value={planEnd}
                onChange={(e) => setPlanEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="op-plan-area">Плановая площадь, га</Label>
            <Input
              id="op-plan-area"
              inputMode="decimal"
              value={planArea}
              onChange={(e) => setPlanArea(e.target.value)}
            />
          </div>

          {!showFact && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="justify-self-start"
              onClick={() => setShowFact(true)}
            >
              Указать факт
            </Button>
          )}

          {showFact && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="op-fact-start">Факт: начало</Label>
                  <Input
                    id="op-fact-start"
                    type="date"
                    value={factStart}
                    onChange={(e) => setFactStart(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="op-fact-end">Факт: окончание</Label>
                  <Input
                    id="op-fact-end"
                    type="date"
                    value={factEnd}
                    onChange={(e) => setFactEnd(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="op-fact-area">Фактическая площадь, га</Label>
                <Input
                  id="op-fact-area"
                  inputMode="decimal"
                  value={factArea}
                  onChange={(e) => setFactArea(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="grid gap-2">
            <Label htmlFor="op-comment">Комментарий</Label>
            <Input
              id="op-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OperationFormDialog;
